// src/admin/QuizStatistics.js - Thống kê điểm thưởng & Tính lại toàn bộ sao (Quiz + Daily Checkin)
import React, { useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { firestore } from '../firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import './QuizStatistics.css';

const QuizStatistics = () => {
  const { allStudentsForOutlet } = useOutletContext();

  // State lưu kết quả tính lại của từng học sinh:
  // { [studentId]: { totalStars, quizStars, dailyStars, attempts, isCalculated: true } }
  const [recalculatedMap, setRecalculatedMap] = useState({});
  const [calculatingIds, setCalculatingIds] = useState({});

  // Trạng thái sắp xếp
  const [sortConfig, setSortConfig] = useState({ key: 'systemStars', direction: 'desc' });

  // 1. Chuẩn bị dữ liệu hiển thị
  const processedData = useMemo(() => {
    if (!Array.isArray(allStudentsForOutlet)) return [];

    return allStudentsForOutlet.map((student) => {
      const customCalc = recalculatedMap[student.id];
      const systemStars = Number(student.totalEarnedStars ?? student.currentStars ?? 0);

      return {
        ...student,
        systemStars,
        recalculated: customCalc || null,
        recalculatedStars: customCalc ? customCalc.totalStars : -1
      };
    });
  }, [allStudentsForOutlet, recalculatedMap]);

  // 2. Thuật toán tính lại toàn bộ sao từ subcollection reward_transactions
  const handleRecalculateSingle = async (studentId) => {
    setCalculatingIds((prev) => ({ ...prev, [studentId]: true }));
    try {
      const transRef = collection(firestore, `students/${studentId}/reward_transactions`);
      const transSnap = await getDocs(transRef);

      let totalQuizStars = 0;
      let totalDailyStars = 0;
      let totalOtherStars = 0;
      let totalAttempts = 0;

      transSnap.docs.forEach((d) => {
        const data = d.data();
        const amount = Number(data.amount || 0);

        if (data.type === 'BIBLE_QUIZ') {
          totalQuizStars += amount;
          totalAttempts += Number(data.attempts || 1);
        } else if (data.type === 'DAILY_CHECKIN') {
          totalDailyStars += amount;
        } else {
          totalOtherStars += amount;
        }
      });

      const totalCalculatedStars = totalQuizStars + totalDailyStars + totalOtherStars;

      setRecalculatedMap((prev) => ({
        ...prev,
        [studentId]: {
          totalStars: totalCalculatedStars,
          quizStars: totalQuizStars,
          dailyStars: totalDailyStars,
          attempts: totalAttempts,
          isCalculated: true
        }
      }));
    } catch (err) {
      console.error(`Lỗi khi tính lại điểm cho học sinh ${studentId}:`, err);
      alert('Không thể tải lịch sử giao dịch của học sinh này.');
    } finally {
      setCalculatingIds((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  // 3. Logic sắp xếp bảng
  const sortedStudents = useMemo(() => {
    let sortableItems = [...processedData];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key] ?? 0;
        let bValue = b[sortConfig.key] ?? 0;

        if (sortConfig.key === 'fullName') {
          const getLastName = (name) => {
            if (!name) return '';
            const parts = name.trim().split(/\s+/);
            return parts[parts.length - 1].toLowerCase();
          };
          aValue = getLastName(a.fullName);
          bValue = getLastName(b.fullName);
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [processedData, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕️';
    return sortConfig.direction === 'asc' ? '🔼' : '🔽';
  };

  return (
    <div className="qs-container">
      <div className="qs-header">
        <h2 className="qs-title">Thống kê Điểm Thưởng & Đố Vui Kinh Thánh</h2>
        <p className="qs-subtitle">
          💡 Cột <strong>"Tổng Sao (Hồ Sơ)"</strong> hiển thị số sao ghi nhận trên hệ thống. Khi cần kiểm tra gian lận, nhấn nút <strong>"Tính lại"</strong> để quét sổ cái giao dịch (gồm sao Đố Vui + sao Nhận Hàng Ngày).
        </p>
      </div>

      <div className="qs-table-wrapper">
        <table className="qs-table">
          <thead>
            <tr>
              <th className="qs-th qs-th-center">STT</th>
              <th className="qs-th" onClick={() => requestSort('holyName')}>
                Tên Thánh {getSortIcon('holyName')}
              </th>
              <th className="qs-th" onClick={() => requestSort('fullName')}>
                Họ và Tên {getSortIcon('fullName')}
              </th>
              <th className="qs-th" onClick={() => requestSort('className')}>
                Lớp {getSortIcon('className')}
              </th>
              <th className="qs-th qs-th-center" onClick={() => requestSort('systemStars')}>
                ⭐ Tổng Sao (Hồ Sơ) {getSortIcon('systemStars')}
              </th>
              <th className="qs-th qs-th-center" onClick={() => requestSort('recalculatedStars')}>
                📊 Kết Quả Tính Lại (Sổ Cái) {getSortIcon('recalculatedStars')}
              </th>
              <th className="qs-th qs-th-center">Thao Tác</th>
            </tr>
          </thead>
          <tbody>
            {sortedStudents.length > 0 ? (
              sortedStudents.map((student, index) => {
                const rec = student.recalculated;
                const isCalculated = Boolean(rec);
                const isMismatch = isCalculated && rec.totalStars !== student.systemStars;

                return (
                  <tr key={student.id} className={`qs-tr ${isMismatch ? 'qs-tr-mismatch' : ''}`}>
                    <td className="qs-td qs-td-center">{index + 1}</td>
                    <td className="qs-td">{student.holyName}</td>
                    <td className="qs-td font-medium">{student.fullName}</td>
                    <td className="qs-td text-sm text-gray-600">{student.className || 'Chưa xếp lớp'}</td>

                    {/* CỘT 1: TỔNG SAO GỐC TRÊN HỒ SƠ */}
                    <td className="qs-td qs-td-center qs-stars-system">
                      <strong>{student.systemStars}</strong> ⭐
                    </td>

                    {/* CỘT 2: KẾT QUẢ TÍNH LẠI RIÊNG BIỆT */}
                    <td className="qs-td qs-td-center">
                      {!isCalculated ? (
                        <span className="qs-tag-uncalc">Chưa đối chiếu</span>
                      ) : (
                        <div className="qs-calc-result-box">
                          <div className={`qs-calc-total ${isMismatch ? 'mismatch' : 'match'}`}>
                            {isMismatch ? '⚠️' : '✓'} <strong>{rec.totalStars}</strong> ⭐
                            {isMismatch && <span className="qs-mismatch-label">(Lệch {rec.totalStars - student.systemStars > 0 ? `+${rec.totalStars - student.systemStars}` : rec.totalStars - student.systemStars})</span>}
                          </div>
                          <div className="qs-calc-breakdown">
                            (Đố vui: {rec.quizStars}⭐ | Chuyên cần: {rec.dailyStars}⭐)
                          </div>
                        </div>
                      )}
                    </td>

                    {/* CỘT 3: NÚT THAO TÁC */}
                    <td className="qs-td qs-td-center">
                      <button
                        onClick={() => handleRecalculateSingle(student.id)}
                        disabled={calculatingIds[student.id]}
                        className={`qs-btn-recalculate ${isCalculated ? 'recalculated' : ''}`}
                        title="Quét lại toàn bộ reward_transactions của thiếu nhi này"
                      >
                        {calculatingIds[student.id] ? '⏳ Đang quét...' : isCalculated ? '🔄 Tính lại' : '🔍 Tính lại'}
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" className="qs-empty-box">
                  Không có dữ liệu thiếu nhi nào.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuizStatistics;